import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  AlertCircle, Check, CheckCircle2, ChevronRight, Circle, CircleDashed,
  Loader2, Pencil, X,
} from 'lucide-react';
import type { FieldDef, FieldState, FieldStateEntry } from '../../orbit/lib/dossier-types';
import { useAutosave, type AutosaveState } from '../../hooks/useAutosave';

/**
 * One editable dossier field row, and the controls behind it.
 *
 * Lifted out of MyBusinessHome so the same row draws a field wherever it is
 * used — the Company Context tab, the section drawer, and the onboarding card
 * flow. One renderer, several homes: a change to how a guessed value looks now
 * lands everywhere at once instead of drifting per surface.
 *
 * Two save behaviours, chosen by the caller:
 *   - default: an explicit Save button, as the settings surfaces have always
 *     worked.
 *   - `autosave`: no Save button at all. The card flow uses this — a card is a
 *     confirmation surface, and a Save button per field would make confirming
 *     ten fields feel like ten forms.
 */

export const STATE_BADGE: Record<FieldState, { icon: React.ReactNode; tone: string; label: string }> = {
  empty:          { icon: <Circle size={12} strokeWidth={2} />,          tone: 'text-gray-300',  label: 'Empty' },
  ai_guessed:     { icon: <CircleDashed size={12} strokeWidth={2.25} />, tone: 'text-brand-500', label: 'AI guess' },
  user_confirmed: { icon: <CheckCircle2 size={12} strokeWidth={2.25} />, tone: 'text-green-600', label: 'Confirmed' },
  user_corrected: { icon: <CheckCircle2 size={12} strokeWidth={2.25} />, tone: 'text-green-600', label: 'Corrected' },
  skipped:        { icon: <Circle size={12} strokeWidth={2} />,          tone: 'text-gray-400',  label: 'Skipped' },
};

export function fmtValue(field: FieldDef, value: unknown): string {
  if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) return '';
  if (field.kind === 'multiselect' && Array.isArray(value)) {
    return value
      .map(v => field.options?.find(o => o.value === v)?.label ?? String(v))
      .join(', ');
  }
  if ((field.kind === 'select' || field.kind === 'chip') && typeof value === 'string') {
    return field.options?.find(o => o.value === value)?.label ?? value;
  }
  if (field.kind === 'date') return fmtDate(value);
  return String(value);
}

/** A stored 'YYYY-MM-DD' read back as a day, in the viewer's locale.
 *  Parsed as parts rather than through `new Date(iso)`, which reads a bare
 *  ISO day as UTC midnight and so prints the day before west of Greenwich. */
export function fmtDate(value: unknown): string {
  if (typeof value !== 'string') return String(value ?? '');
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!m) return value;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Coerce a stored multiselect value to a string list.
 *
 * Multiselects are normally arrays, but extraction (voice, paste, older rows)
 * can land a plain string or a comma-separated one — so every reader has to
 * tolerate all three or `.map` throws on real data.
 */
export function toStringArray(v: unknown): string[] {
  if (Array.isArray(v)) return v.map(x => String(x)).filter(Boolean);
  if (typeof v === 'string' && v.trim()) {
    return v.split(',').map(s => s.trim()).filter(Boolean);
  }
  return [];
}

/** Whether a value counts as answered. Mirrors the backend's notion of filled. */
export function isFilledValue(v: unknown): boolean {
  if (v == null) return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'string') return v.trim().length > 0;
  return true;
}

/**
 * How far through a section a company actually is.
 *
 * One helper because the card header and the flow's step dots each used to
 * count for themselves, and the moment optional fields stopped counting they
 * would have disagreed: a card reading "done" under a grey dot.
 *
 * An optional field is not part of the total. Leaving it blank is a complete
 * answer, so counting it as missing meant a finished section sat at 2 of 3 with
 * nothing the person could do about it. Filling one still shows, through the
 * row's own state icon.
 */
export function sectionProgress(
  fields: { key: string; optional?: boolean }[],
  values: Record<string, unknown>,
): { total: number; filled: number; pct: number; done: boolean } {
  const counted = fields.filter(f => !f.optional);
  const total = counted.length;
  const filled = counted.filter(f => isFilledValue(values[subKey(f.key)])).length;
  return {
    total,
    filled,
    pct: total ? Math.round((100 * filled) / total) : 100,
    done: total > 0 && filled >= total,
  };
}

/** "a.company_name" → "company_name". Values are stored per section, unprefixed. */
export function subKey(fullKey: string): string {
  const i = fullKey.indexOf('.');
  return i === -1 ? fullKey : fullKey.slice(i + 1);
}

export type CommitAction = 'confirm' | 'correct';
export type CommitFn = (value: unknown, action: CommitAction) => void | Promise<unknown>;

export interface EditableFieldProps {
  field: FieldDef;
  value: unknown;
  stateEntry?: FieldStateEntry;
  onCommit: CommitFn;
  /** Save a beat after the last keystroke instead of on a Save button. */
  autosave?: boolean;
}

export const EditableField: React.FC<EditableFieldProps> = ({
  field, value, stateEntry, onCommit, autosave = false,
}) => {
  const [editing, setEditing] = useState(false);
  const state: FieldState = (stateEntry?.state as FieldState) || 'empty';
  const badge = STATE_BADGE[state];

  const valueStr = useMemo(() => fmtValue(field, value), [field, value]);
  const hasValue = valueStr.length > 0;
  // An optional row nobody filled is not an outstanding task, so it must not
  // read like one: muted label, muted marker, no unchecked circle competing for
  // attention with the rows that do need an answer.
  const blankOptional = Boolean(field.optional) && !hasValue;
  const actionFor = (): CommitAction =>
    (state === 'user_confirmed' || state === 'user_corrected') ? 'correct' : 'confirm';

  // Read mode
  if (!editing) {
    return (
      <div className="group flex items-start gap-2.5 px-4 py-3 transition-colors hover:bg-gray-50">
        <span
          className={clsx('mt-0.5 flex-none', blankOptional ? 'text-gray-200' : badge.tone)}
          aria-label={blankOptional ? 'Optional, not filled' : badge.label}
        >
          {badge.icon}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className={clsx('text-sm font-medium', blankOptional ? 'text-gray-500' : 'text-gray-900')}>
              {field.label}
            </span>
            {field.optional && (
              <span className="rounded-sm bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
                optional
              </span>
            )}
            {field.required_for_routing && state !== 'user_confirmed' && state !== 'user_corrected' && (
              <span className="rounded-sm bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                routing
              </span>
            )}
          </div>
          {hasValue ? (
            <p className="mt-0.5 break-words text-xs leading-snug text-gray-700">
              {valueStr}
              {state === 'ai_guessed' && stateEntry?.confidence != null && (
                <span className="ml-1.5 rounded bg-brand-50 px-1.5 py-0.5 text-[10px] font-medium text-brand-700">
                  AI · {Math.round(stateEntry.confidence * 100)}%
                </span>
              )}
            </p>
          ) : (
            <p className="mt-0.5 font-serif text-xs italic text-gray-400">
              {field.placeholder ?? 'Not filled yet.'}
            </p>
          )}
        </div>
        {/* A guess needs one tap to become an answer. Opening the editor just to
            close it wrote nothing, so the row kept its AI badge and the person
            had no way to say "yes, that's right" without retyping it. */}
        {state === 'ai_guessed' && hasValue && (
          <button
            type="button"
            onClick={() => { void Promise.resolve(onCommit(value, 'confirm')); }}
            className="flex-none rounded-md px-2 py-1 text-[11px] font-semibold text-brand-600 ring-1 ring-brand-200 transition-colors hover:bg-brand-50"
          >
            That's right
          </button>
        )}
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="flex-none rounded-md p-1.5 text-gray-400 opacity-0 transition-opacity hover:bg-gray-100 hover:text-brand-500 group-hover:opacity-100"
          aria-label={`Edit ${field.label}`}
        >
          <Pencil size={12} strokeWidth={2.25} />
        </button>
      </div>
    );
  }

  // Edit mode — render kind-specific control
  return (
    <div className="bg-brand-50/40 px-4 py-3">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <span className="text-sm font-semibold text-gray-900">{field.label}</span>
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="rounded-md p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-700"
          aria-label={autosave ? 'Done editing' : 'Cancel edit'}
        >
          <X size={13} strokeWidth={2.25} />
        </button>
      </div>
      {field.why && (
        <p className="mb-2 font-serif text-[11px] italic leading-relaxed text-gray-500">
          Why I ask. {field.why}
        </p>
      )}
      <EditControl
        field={field}
        value={value}
        autosave={autosave}
        onCommit={onCommit}
        actionFor={actionFor}
        onDone={() => setEditing(false)}
      />
    </div>
  );
};

interface ControlProps {
  field: FieldDef;
  value: unknown;
  autosave: boolean;
  onCommit: CommitFn;
  actionFor: () => CommitAction;
  onDone: () => void;
}

const EditControl: React.FC<ControlProps> = (props) => {
  const { field } = props;

  if (field.kind === 'select' || field.kind === 'chip') {
    return <SingleSelectControl {...props} />;
  }
  if (field.kind === 'multiselect') {
    return <MultiSelectControl {...props} />;
  }
  if (field.kind === 'date') {
    return <DateControl {...props} />;
  }
  return <TextControl {...props} multiline={field.kind === 'longtext'} />;
};

/** A pick is already a deliberate act, so it saves on click in both modes.
 *  Autosave only changes whether the row then closes itself. */
const SingleSelectControl: React.FC<ControlProps> = ({
  field, value, onCommit, actionFor, onDone,
}) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pick = async (v: string) => {
    setSaving(true);
    setError(null);
    try {
      await onCommit(v, actionFor());
      onDone();
    } catch (e) {
      setError(errText(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="flex flex-wrap gap-1.5">
        {(field.options ?? []).map(o => {
          const active = o.value === value;
          return (
            <button
              key={o.value}
              type="button"
              disabled={saving}
              onClick={() => { void pick(o.value); }}
              className={clsx(
                'rounded-full border px-3 py-1 text-xs font-medium transition-colors disabled:opacity-60',
                active
                  ? 'border-brand-500 bg-brand-500 text-white'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-brand-500 hover:bg-brand-50 hover:text-brand-700',
              )}
            >
              {o.label}
            </button>
          );
        })}
      </div>
      {error && <InlineError message={error} />}
    </>
  );
};

const MultiSelectControl: React.FC<ControlProps> = ({
  field, value, autosave, onCommit, actionFor, onDone,
}) => {
  const seed = useMemo(() => (Array.isArray(value) ? (value as string[]) : []), [value]);

  const save = React.useCallback(
    (v: string[]) => Promise.resolve(onCommit(v, actionFor())),
    [onCommit, actionFor],
  );
  const auto = useAutosave<string[]>({
    value: seed,
    onSave: save,
    isEqual: sameStringSet,
    disabled: !autosave,
  });

  const [manual, setManual] = useState<string[]>(seed);
  const picked = autosave ? auto.draft : manual;

  const toggle = (v: string) => {
    const next = picked.includes(v) ? picked.filter(x => x !== v) : [...picked, v];
    if (autosave) auto.set(next);
    else setManual(next);
  };

  return (
    <>
      <div className="flex flex-wrap gap-1.5">
        {(field.options ?? []).map(o => {
          const active = picked.includes(o.value);
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => toggle(o.value)}
              className={clsx(
                'inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium transition-colors whitespace-nowrap',
                active
                  ? 'border-brand-500 bg-brand-500 text-white'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-brand-500 hover:bg-brand-50 hover:text-brand-700',
              )}
            >
              {active && <Check size={11} strokeWidth={2.5} />}
              {o.label}
            </button>
          );
        })}
      </div>
      {autosave ? (
        <SaveHint state={auto.state} error={auto.error} />
      ) : (
        <button
          type="button"
          onClick={() => { void Promise.resolve(onCommit(manual, actionFor())).then(onDone); }}
          disabled={manual.length === 0}
          className="mt-3 inline-flex items-center gap-1 rounded-md bg-gray-900 px-3.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-brand-500 disabled:cursor-not-allowed disabled:bg-gray-300 whitespace-nowrap"
        >
          Done · {manual.length} selected
          <ChevronRight size={12} strokeWidth={2.5} />
        </button>
      )}
    </>
  );
};

/** The native picker. A partly-typed date is not a value, so the input is held
 *  local and only committed once it parses, whether autosaving or not. */
const DateControl: React.FC<ControlProps> = ({
  field, value, autosave, onCommit, actionFor, onDone,
}) => {
  const [draft, setDraft] = useState<string>(typeof value === 'string' ? value.slice(0, 10) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const commit = async (iso: string) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return;
    setSaving(true);
    setError(null);
    try {
      await onCommit(iso, actionFor());
      onDone();
    } catch (e) {
      setError(errText(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <input
        type="date"
        value={draft}
        disabled={saving}
        onChange={e => {
          setDraft(e.target.value);
          if (autosave) void commit(e.target.value);
        }}
        className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
      />
      {error && <InlineError message={error} />}
      {!autosave && (
        <button
          type="button"
          onClick={() => { void commit(draft); }}
          disabled={saving || !/^\d{4}-\d{2}-\d{2}$/.test(draft)}
          className="mt-2 inline-flex items-center gap-1 rounded-md bg-gray-900 px-3.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-brand-500 disabled:cursor-not-allowed disabled:bg-gray-300 whitespace-nowrap"
        >
          Save
          <ChevronRight size={12} strokeWidth={2.5} />
        </button>
      )}
    </>
  );
};

const TextControl: React.FC<ControlProps & { multiline?: boolean }> = ({
  field, value, multiline, autosave, onCommit, actionFor, onDone,
}) => {
  const asText = value == null ? '' : String(value);

  const coerce = React.useCallback(
    (raw: string): unknown => {
      const trimmed = raw.trim();
      if (field.kind === 'number') {
        const n = Number(trimmed);
        return Number.isFinite(n) ? n : null;
      }
      return trimmed;
    },
    [field.kind],
  );

  const save = React.useCallback(
    (raw: string) => {
      const v = coerce(raw);
      if (v == null) return Promise.resolve();
      return Promise.resolve(onCommit(v, actionFor()));
    },
    [coerce, onCommit, actionFor],
  );

  const auto = useAutosave<string>({
    value: asText,
    onSave: save,
    // A blank box is someone clearing the field to retype, not a value to
    // store — hold the write until there is something to save.
    isSavable: raw => raw.trim().length > 0 && coerce(raw) != null,
    disabled: !autosave,
  });

  const [manual, setManual] = useState<string>(asText);
  const draft = autosave ? auto.draft : manual;
  const setDraft = (v: string) => { if (autosave) auto.set(v); else setManual(v); };

  // Opening a guess and leaving it as it stands is a person accepting it.
  //
  // The autosave only writes CHANGES, so an AI-guessed row that someone opened,
  // read and closed was never written: it kept its "AI · 85%" badge however many
  // times they went in, which reads as an edit that did not save. The chat has
  // always had this ("I've got X. Is that right?"); the card row did not.
  //
  // Deliberately narrow. It fires only when nothing was typed and nothing is in
  // flight (`idle`), only while the value is still not the person's own
  // (`actionFor` says confirm), and only when the draft still matches what the
  // server holds — so it can never overwrite an edit the autosave owns.
  const acceptGuessIfUntouched = () => {
    if (!autosave) return;
    if (auto.state !== 'idle') return;
    if (actionFor() !== 'confirm') return;
    const v = coerce(draft);
    if (v == null || (typeof v === 'string' && !v)) return;
    if (String(v) !== asText.trim()) return;
    void Promise.resolve(onCommit(v, 'confirm'));
  };

  const onLeave = () => {
    auto.flush();
    acceptGuessIfUntouched();
  };

  const commitManual = () => {
    const v = coerce(manual);
    if (v == null || (typeof v === 'string' && !v)) return;
    void Promise.resolve(onCommit(v, actionFor())).then(onDone);
  };

  const shared = 'w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

  return (
    <>
      {multiline ? (
        <textarea
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={autosave ? onLeave : undefined}
          placeholder={field.placeholder ?? ''}
          rows={3}
          className={clsx(shared, 'resize-none')}
        />
      ) : (
        <input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={autosave ? onLeave : undefined}
          onKeyDown={e => {
            if (e.key !== 'Enter') return;
            if (autosave) onLeave();
            else commitManual();
          }}
          placeholder={field.placeholder ?? ''}
          type={field.kind === 'number' ? 'number' : 'text'}
          className={shared}
        />
      )}
      {autosave ? (
        <SaveHint state={auto.state} error={auto.error} />
      ) : (
        <button
          type="button"
          onClick={commitManual}
          disabled={!manual.trim()}
          className="mt-2 inline-flex items-center gap-1 rounded-md bg-gray-900 px-3.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-brand-500 disabled:cursor-not-allowed disabled:bg-gray-300 whitespace-nowrap"
        >
          Save
          <ChevronRight size={12} strokeWidth={2.5} />
        </button>
      )}
    </>
  );
};

/** The only feedback an autosaving row gets. Quiet when idle — a permanent
 *  status line on every field would be noisier than the Save button it replaced. */
export const SaveHint: React.FC<{ state: AutosaveState; error: string | null }> = ({ state, error }) => {
  if (state === 'error') return <InlineError message={error ?? "Couldn't save that."} />;
  if (state === 'saving') {
    return (
      <p className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-400">
        <Loader2 size={11} strokeWidth={2.5} className="animate-spin" />
        Saving
      </p>
    );
  }
  if (state === 'saved') {
    return (
      <p className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-medium text-green-600">
        <Check size={11} strokeWidth={3} />
        Saved
      </p>
    );
  }
  return null;
};

const InlineError: React.FC<{ message: string }> = ({ message }) => (
  <p role="alert" className="mt-2 inline-flex items-start gap-1.5 text-[11px] font-medium text-red-600">
    <AlertCircle size={11} strokeWidth={2.5} className="mt-px flex-none" />
    <span>{message} Your previous value is back.</span>
  </p>
);

function sameStringSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sb = new Set(b);
  return a.every(x => sb.has(x));
}

function errText(e: unknown): string {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (e instanceof Error && e.message) return e.message;
  return "Couldn't save that.";
}
